import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Material Sharp Icons as React Components
const PictureAsPdf = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M20 2H8L2 8v12h18V2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v1h1.5v1.5H17.5V7h3v1.5zM9 9.5h1v-1H9v1z"/>
  </svg>
);

const Chat = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M20 2H4L2 4v18l4-4h14V2z"/>
  </svg>
);

const InsertDriveFile = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M14 2H4v20h16V8l-6-6zm4 18H6V4h7v5h5v11z"/>
  </svg>
);

const Mail = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M22 4H2v16h20V4zM4 6l8 5 8-5v2l-8 5-8-5V6z"/>
  </svg>
);

const Assessment = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M21 3H3v18h18V3zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const TableChart = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M10 10h5v11h-5zm7 11h5V10h-5zM22 3H2v5h20zM2 10v11h5V10z"/>
  </svg>
);

const Folder = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M10 4H2v16h20V6h-8l-2-2z"/>
  </svg>
);

const PieChart = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10zm2 0v9h9c-.47-4.74-4.25-8.53-9-9zm0 11v9c4.75-.47 8.53-4.25 9-9h-9z"/>
  </svg>
);

const Airplay = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M6 22h12l-6-6zM23 3H1v16h4v-2H3V5h18v12h-2v2h4V3z"/>
  </svg>
);

const Videocam = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M17 10.5V6H3v12h14v-4.5l4 4v-11l-4 4z"/>
  </svg>
);

const Subtitles = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M22 4H2v16h20V4zM4 12h4v2H4v-2zm10 6H4v-2h10v2zm6 0h-4v-2h4v2zm0-4H10v-2h10v2z"/>
  </svg>
);

const AccountBox = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M21 3H3v18h18V3zm-9 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm6 10H6v-1c0-2 4-3.1 6-3.1s6 1.1 6 3.1v1z"/>
  </svg>
);

const FolderShared = ({ size = 24, color = "#BCD6FF" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M22 6h-8l-2-2H2v16h20V6zm-5 3c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm4 8h-8v-1c0-1.33 2.67-2 4-2s4 .67 4 2v1z"/>
  </svg>
);

interface FloatingIcon {
  id: string;
  iconComponent: React.ComponentType<any>;
  startX: number;
  startY: number;
  delay: number;
  duration: number;
  scale: number;
  opacity: number;
}

export const BackgroundFloatingIcons: React.FC = () => {
  const [floatingIcons, setFloatingIcons] = useState<FloatingIcon[]>([]);

  // Memoize constants
  const constants = useMemo(() => ({
    ICONS: [
      PictureAsPdf, Chat, InsertDriveFile, Mail, Assessment, TableChart, 
      Folder, PieChart, Airplay, Videocam, Subtitles, AccountBox, FolderShared
    ],
    SPAWN_INTERVAL: 1500,
    ANIMATION_DURATION: 5,
  }), []);

  // Create floating icon
  const createFloatingIcon = useCallback(() => {
    const randomIcon = constants.ICONS[Math.floor(Math.random() * constants.ICONS.length)];
    const startY = Math.random() * window.innerHeight;
    const startX = -80; // Start from left edge
    
    const newIcon: FloatingIcon = {
      id: `icon-${Date.now()}-${Math.random()}`,
      iconComponent: randomIcon,
      startX,
      startY,
      delay: 0,
      duration: constants.ANIMATION_DURATION + Math.random() * 2, // Reduced randomization
      scale: 1.0 + Math.random() * 0.4, // Reduced scale variation for performance
      opacity: 0.4 + Math.random() * 0.3
    };
    
    setFloatingIcons(prev => [...prev, newIcon]);
  }, [constants]);

  // Spawn icons continuously
  useEffect(() => {
    let mounted = true;
    
    // Initial spawn
    createFloatingIcon();
    
    const interval = setInterval(() => {
      if (!mounted) return;
      createFloatingIcon();
      
      // Cleanup old icons
      const now = Date.now();
      setFloatingIcons(prev => prev.filter(icon => 
        now - parseInt(icon.id.split('-')[1]) < (constants.ANIMATION_DURATION + 3) * 1000
      ));
    }, constants.SPAWN_INTERVAL);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [createFloatingIcon, constants]);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      <AnimatePresence>
        {floatingIcons.map((icon) => {
          const IconComponent = icon.iconComponent;
          
          return (
            <motion.div
              key={icon.id}
              className="absolute"
              style={{
                left: icon.startX,
                top: icon.startY,
              }}
              initial={{
                x: 0,
                y: 0,
                scale: icon.scale,
                opacity: 0
              }}
              animate={{
                // Flow from left towards document position (10% more to the right)
                x: window.innerWidth * 1.0 + (Math.random() - 0.5) * 50,
                // Target middle 30% of screen height (35% to 65%)
                y: (window.innerHeight * 0.5) - icon.startY + (Math.random() - 0.5) * (window.innerHeight * 0.3),
                scale: icon.scale,
                opacity: [0, 1, 1, 0]
              }}
              exit={{
                scale: 0,
                opacity: 0
              }}
              transition={{
                duration: icon.duration,
                ease: "linear",
                times: [0, 0.2, 0.8, 1]
              }}
            >
                              <IconComponent 
                  size={24} 
                  color="#BCD6FF"
                />
            </motion.div>
          );
        })}
      </AnimatePresence>
      
      {/* Ambient particles */}
      {Array.from({ length: 20 }).map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-2 h-2 bg-blue-400/20 rounded-full"
          style={{
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
          }}
          animate={{
            x: [0, Math.random() * 100 - 50],
            y: [0, Math.random() * 100 - 50],
            opacity: [0, 0.6, 0],
            scale: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 8 + Math.random() * 4,
            repeat: Infinity,
            delay: Math.random() * 4,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
}; 